import React from 'react';

export const BowTieHistory = () => (
  <div className="prose prose-lg max-w-none">
    <h4 className="text-xl font-serif font-bold text-[#001B2A] mb-4">When Were Bow Ties Invented?</h4>
    <p>
      The origin of the bow tie dates back to a particular military unit that fought in the <strong>Thirty Years War of the 1600s</strong>. Despite the unit consisting of men from numerous Eastern European countries, the soldiers were commonly known as the <em>Croats, Crabats</em> or, in Serbian, <em>Hravat</em>.
    </p>

    <p>
      The battle dress the Croats proudly wore was remarkable - fur hats, long red or black cloaks, and <strong>scarves tied at the neck</strong>. The rank and file wore scarves made from coarse fabric while the officers' scarves were silk or cotton.
    </p>

    <div className="bg-[#E6D7B3]/20 border-l-4 border-[#7B1E2B] p-6 my-6 rounded-r-lg">
      <p className="font-semibold text-[#001B2A] mb-2">Croatian Folklore:</p>
      <p className="italic">
        According to tradition, women tied scarves around the necks of their loved ones as they left for war as a token of love and fidelity.
      </p>
    </div>

    <h4 className="text-xl font-serif font-bold text-[#001B2A] mb-4 mt-8">From the Battle Field to Fashion Accessory</h4>
    <p>
      Perhaps the scarves served a function, keeping shirts closed or contributing to hygiene. Maybe they were primarily symbolic - safeguarding the neck during battle. Or perhaps they were sentimental tokens of love.
    </p>

    <p>
      Whatever their purpose - <strong>the scarves made an impression</strong>. Following the soldiers' service in France under the Royal Cravates regiment, the French took the accessory into high society where it was, appropriately, called <em>la cravate</em>.
    </p>

    <p>
      Some argue that the cravat led to the necktie and then the bow tie. Others say it was the opposite - that the Croat fashion was first tied into a bow, and later lengthened into the necktie. Either way, Croatians are intensely proud of their association with the cravat. In <strong>2008, the Croatian parliament declared October 18 as annual Cravat Day</strong>.
    </p>

    <h4 className="text-xl font-serif font-bold text-[#001B2A] mb-4 mt-8">When Did The Bow Tie Become Popular?</h4>
    <p>
      Adornment at the neck remained a staple in menswear for more than two centuries. But it wasn't until the <strong>1800s</strong> that the bow tie as we know it today became popular.
    </p>

    <div className="bg-gradient-to-r from-[#001B2A] to-[#7B1E2B] text-white p-6 rounded-lg my-6">
      <h5 className="font-serif font-bold text-lg mb-3">The Tuxedo Park Revolution</h5>
      <p>
        In <strong>1886</strong>, <em>Pierre Lorillard IV</em> broke the mold when he wore a black tie to an event at his family's estate in Tuxedo Park, NY. Until then, fashion dictated that men wear white tie and tails to formal gatherings.
      </p>
      <p className="mt-3">
        As heir of the Lorillard Tobacco Company fortune, Lorillard rubbed shoulders with New York's elite, including the Vanderbilts and the Astors. His appearance in "black tie" was both noticed and immediately emulated.
      </p>
    </div>

    <p>
      Bow ties soon became the "it" accessory, and by the time the 20s roared into history, they served as a signature piece for people of all persuasions - from <strong>Winston Churchill and Charlie Chaplain</strong>, to <strong>Marlene Dietrich and Frank Sinatra</strong>.
    </p>

    <p>
      Each bow tie wearer of the time made the accessory their own, experimenting with fabrics, knots, and complementary accessories. In the 30s, the single end bow tie became top choice for the most formal of events, and the standard bow tie marched on, boldly boasting its day-to-day elegance and versatility.
    </p>

    <h4 className="text-xl font-serif font-bold text-[#001B2A] mb-4 mt-8">The Bow Tie Today</h4>
    <p>
      More than <strong>three centuries</strong> since its first appearance, and a hundred strong in its modern-day iteration, the bow tie is clearly here to stay.
    </p>

    <p>
      Today's bow tie enthusiasts wear their bows to events both formal and casual, some following bow tie rules to the letter and others blazing a fashion trail of their own.
    </p>
  </div>
);
